import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Eye, Save, Send } from 'lucide-react';
import { ImageUploadField, StringListField, ComponentListField } from '../components/admin/HowItWorksFieldEditors';
import HowItWorksContentRenderer from '../components/how-it-works/HowItWorksContentRenderer';

const AdminHowItWorksEditorPage = () => {
  const { topicId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [topic, setTopic] = useState(null);

  const fetchTopic = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi.getHowItWorksTopicAdmin(topicId);
      setTopic(data);
    } catch (err) {
      console.error('Error fetching how-it-works topic:', err);
      toast.error('Failed to load topic');
      navigate('/admin/how-it-works');
    } finally {
      setLoading(false);
    }
  }, [topicId, navigate]);

  useEffect(() => {
    fetchTopic();
  }, [fetchTopic]);

  const updateField = (field, value) => setTopic((prev) => ({ ...prev, [field]: value }));

  const updateRelatedArticle = (field, value) => {
    setTopic((prev) => ({
      ...prev,
      related_article: { ...(prev.related_article || { article_id: '', title: '' }), [field]: value },
    }));
  };

  const buildPayload = () => ({
    category: topic.category?.trim() || null,
    title: topic.title.trim(),
    short_description: topic.short_description.trim(),
    what_it_is: topic.what_it_is.trim(),
    where_its_used: topic.where_its_used.trim(),
    concept: topic.concept.trim(),
    architecture_image: topic.architecture_image || null,
    architecture_image_alt: topic.architecture_image_alt?.trim() || null,
    architecture_components: (topic.architecture_components || [])
      .filter((c) => c.name.trim() || c.detail.trim())
      .map((c) => ({ name: c.name.trim(), detail: c.detail.trim() })),
    step_by_step_image: topic.step_by_step_image || null,
    step_by_step_image_alt: topic.step_by_step_image_alt?.trim() || null,
    step_by_step_steps: (topic.step_by_step_steps || []).map((s) => s.trim()).filter(Boolean),
    failure_signs: (topic.failure_signs || []).map((s) => s.trim()).filter(Boolean),
    related_article: topic.related_article?.article_id?.trim() && topic.related_article?.title?.trim()
      ? { article_id: topic.related_article.article_id.trim(), title: topic.related_article.title.trim() }
      : null,
  });

  const handleSave = async (publish) => {
    if (!topic.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!(topic.failure_signs || []).some((s) => s.trim())) {
      toast.error('At least one Common Failure Sign is required');
      return;
    }
    const setBusy = publish ? setPublishing : setSaving;
    setBusy(true);
    try {
      const payload = { ...buildPayload(), status: publish ? 'PUBLISHED' : 'DRAFT' };
      const updated = await adminApi.updateHowItWorksTopic(topicId, payload);
      setTopic(updated);
      toast.success(publish ? 'Published' : 'Draft saved');
    } catch (err) {
      console.error('Error saving how-it-works topic:', err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  if (loading || !topic) {
    return (
      <div className="min-h-[calc(100vh-4rem)] py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-4">
          <div className="h-8 w-1/2 bg-muted rounded animate-pulse" />
          <div className="h-32 w-full bg-muted rounded animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] py-8 px-4" data-testid="admin-how-it-works-editor-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/admin/how-it-works">
            <Button variant="ghost" size="icon" data-testid="back-to-how-it-works-list-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-chivo text-2xl font-black uppercase tracking-tight truncate">{topic.title || 'Untitled'}</h1>
            <p className="text-muted-foreground text-sm font-mono">{topic.topic_id} · {topic.status}</p>
          </div>
          <Button variant="outline" onClick={() => setShowPreview(true)} className="gap-2" data-testid="preview-how-it-works-btn">
            <Eye className="h-4 w-4" /> Preview
          </Button>
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving || publishing} className="gap-2" data-testid="save-draft-how-it-works-btn">
            <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Draft'}
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving || publishing} className="gap-2" data-testid="publish-how-it-works-btn">
            <Send className="h-4 w-4" /> {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        </div>

        <Card className="border-2 mb-6">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label htmlFor="hiwe-title">Title *</Label>
              <Input id="hiwe-title" value={topic.title} onChange={(e) => updateField('title', e.target.value)} />
            </div>
            <div>
              <Label htmlFor="hiwe-short-description">Short Description *</Label>
              <Input
                id="hiwe-short-description"
                value={topic.short_description}
                onChange={(e) => updateField('short_description', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="hiwe-category">Category</Label>
              <Input id="hiwe-category" value={topic.category || ''} onChange={(e) => updateField('category', e.target.value)} />
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card className="border-2">
            <CardHeader className="pb-3"><CardTitle className="font-chivo text-base uppercase">1. What It Is</CardTitle></CardHeader>
            <CardContent>
              <Textarea rows={3} value={topic.what_it_is} onChange={(e) => updateField('what_it_is', e.target.value)} />
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="pb-3"><CardTitle className="font-chivo text-base uppercase">2. Where It's Used</CardTitle></CardHeader>
            <CardContent>
              <Textarea rows={3} value={topic.where_its_used} onChange={(e) => updateField('where_its_used', e.target.value)} />
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="pb-3"><CardTitle className="font-chivo text-base uppercase">3. The Concept</CardTitle></CardHeader>
            <CardContent>
              <Textarea rows={5} value={topic.concept} onChange={(e) => updateField('concept', e.target.value)} />
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="pb-3"><CardTitle className="font-chivo text-base uppercase">4. Architecture / Components</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <ImageUploadField
                label="Diagram"
                value={topic.architecture_image}
                onChange={(v) => updateField('architecture_image', v)}
                folder="how-it-works"
              />
              <div>
                <Label htmlFor="hiwe-architecture-alt">Diagram Alt Text</Label>
                <Input
                  id="hiwe-architecture-alt"
                  value={topic.architecture_image_alt || ''}
                  onChange={(e) => updateField('architecture_image_alt', e.target.value)}
                />
              </div>
              <ComponentListField
                items={topic.architecture_components || []}
                onChange={(v) => updateField('architecture_components', v)}
              />
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="pb-3"><CardTitle className="font-chivo text-base uppercase">5. Step-by-Step Working</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <ImageUploadField
                label="Diagram"
                value={topic.step_by_step_image}
                onChange={(v) => updateField('step_by_step_image', v)}
                folder="how-it-works"
              />
              <div>
                <Label htmlFor="hiwe-steps-alt">Diagram Alt Text</Label>
                <Input
                  id="hiwe-steps-alt"
                  value={topic.step_by_step_image_alt || ''}
                  onChange={(e) => updateField('step_by_step_image_alt', e.target.value)}
                />
              </div>
              <StringListField
                label="Steps"
                items={topic.step_by_step_steps || []}
                onChange={(v) => updateField('step_by_step_steps', v)}
                placeholder="Describe this step"
                addLabel="Add Step"
              />
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="pb-3"><CardTitle className="font-chivo text-base uppercase">6. Common Failure Signs *</CardTitle></CardHeader>
            <CardContent>
              <StringListField
                label="Signs"
                items={topic.failure_signs || []}
                onChange={(v) => updateField('failure_signs', v)}
                placeholder="Describe a failure sign"
                addLabel="Add Failure Sign"
              />
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="pb-3"><CardTitle className="font-chivo text-base uppercase">7. Related Article (optional)</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hiwe-related-id">Article ID</Label>
                <Input
                  id="hiwe-related-id"
                  placeholder="hvil"
                  value={topic.related_article?.article_id || ''}
                  onChange={(e) => updateRelatedArticle('article_id', e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">Links to /articles/{topic.related_article?.article_id || '...'}</p>
              </div>
              <div>
                <Label htmlFor="hiwe-related-title">Link Text</Label>
                <Input
                  id="hiwe-related-title"
                  value={topic.related_article?.title || ''}
                  onChange={(e) => updateRelatedArticle('title', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-[95vw] w-full max-h-[92vh] overflow-y-auto p-0">
          <HowItWorksContentRenderer topic={{ ...topic, ...buildPayload() }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminHowItWorksEditorPage;
